import { Core, WebViewerInstance } from '@pdftron/webviewer';
import WebViewerWrapper from 'pdftron/core/WebViewerWrapper';
import { PolygonZoneCustomData, DocumentTypes, PDFTronTools } from 'types';
import { getIframeDocument } from './CropToolUtils';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';

/**
 * Determines if a point is inside a polygon using the ray casting algorithm.
 * Casts a ray from the test point and counts edge intersections - odd count means inside.
 */
export function pointInPolygon(point: { x: number; y: number }, polygon: { x: number; y: number }[]): boolean {
  const { x: testX, y: testY } = point;
  let inside = false;

  // Iterate through each edge of the polygon
  for (
    let currentIndex = 0, previousIndex = polygon.length - 1;
    currentIndex < polygon.length;
    previousIndex = currentIndex++
  ) {
    const currentX = polygon[currentIndex].x;
    const currentY = polygon[currentIndex].y;
    const previousX = polygon[previousIndex].x;
    const previousY = polygon[previousIndex].y;

    // Check if ray from test point intersects with current edge
    if (
      currentY > testY !== previousY > testY &&
      testX < ((previousX - currentX) * (testY - currentY)) / (previousY - currentY) + currentX
    ) {
      inside = !inside; // Toggle inside/outside status
    }
  }

  return inside;
}

export function getPagePolygonZone(instance: WebViewerInstance | undefined, currentPage: number) {
  if (!instance) return;
  return instance.Core.annotationManager.getAnnotationsList().find((annot: any) => {
    return (
      annot.getCustomData(PolygonZoneCustomData.polygonAnnotation) === 'true' &&
      // Only return user-created polygon crops (not layer/separation shapes)
      annot.getCustomData(PolygonZoneCustomData.shapePolygon) !== 'true' &&
      annot.PageNumber === currentPage
    );
  });
}

export function getAllPagePolygonZones(instance: WebViewerInstance | undefined, currentPage: number) {
  if (!instance) return [];
  return instance.Core.annotationManager.getAnnotationsList().filter((annot: any) => {
    return (
      annot.getCustomData(PolygonZoneCustomData.polygonAnnotation) === 'true' &&
      // Include both regular polygon crops AND layer/separation shapes in coordinated overlay system
      annot.PageNumber === currentPage
    );
  });
}

export function cursorIsInsidePolygonZone(
  mouseEvent: { clientX: number; clientY: number },
  polygonZone: Core.Annotations.PolygonAnnotation,
  wrapper: WebViewerWrapper,
  currentPage: number,
  addBuffer = false,
): boolean {
  const polygonPoints = polygonZone.getPath();
  if (!polygonPoints || polygonPoints.length === 0) {
    return false;
  }

  const { instance } = wrapper;
  // Get cursor position (adjusted for scale and offsets)
  const iframeDoc = getIframeDocument(wrapper, currentPage);
  if (!iframeDoc) return false;
  const zoomLevel = instance.UI.getZoomLevel();
  const rect = iframeDoc.getBoundingClientRect();
  const cursorX = (mouseEvent.clientX - rect.left + iframeDoc.scrollLeft) / zoomLevel;
  const cursorY = (mouseEvent.clientY - rect.top + iframeDoc.scrollTop) / zoomLevel;

  const cursorPoint = { x: cursorX, y: cursorY };

  // First check if cursor is inside the actual polygon
  const isInsidePolygon = pointInPolygon(cursorPoint, polygonPoints);
  if (isInsidePolygon) {
    return true;
  }

  // If not inside polygon and no buffer requested, return false
  if (!addBuffer) {
    return false;
  }

  // Add buffer zone around polygon (similar to crop zones)
  let baseBuffer = 75;
  const buffer = baseBuffer / zoomLevel; // Adjust buffer based on zoom level

  // Calculate polygon bounding box
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  polygonPoints.forEach((point) => {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  });

  // Check if cursor is within the buffered bounding box
  return cursorX >= minX - 5 && cursorX <= maxX + 5 && cursorY >= minY - buffer && cursorY <= maxY + buffer;
}

export function getPolygonPoints(annotation: Core.Annotations.PolygonAnnotation) {
  return annotation.getPath ? annotation.getPath().map((point: any) => [point.x, point.y] as [number, number]) : [];
}

export function hasMultiplePolygonsOnPage(annotationManager: any, pageNumber: number): boolean {
  if (!annotationManager) return false;
  const annotations = annotationManager.getAnnotationsList?.() || [];
  const count = annotations.filter(
    (a: Core.Annotations.Annotation) =>
      a.getCustomData(PolygonZoneCustomData.polygonAnnotation) === 'true' &&
      a.getCustomData(PolygonZoneCustomData.shapePolygon) !== 'true' &&
      a.PageNumber === pageNumber,
  ).length;
  return count > 1;
}

export function validatePolygonCrop(annotation: Core.Annotations.Annotation): boolean {
  // Must have at least 3 points
  const pathPoints = (annotation as Core.Annotations.PolygonAnnotation).getPath?.() || [];
  if (pathPoints.length < 3) return false;
  return true;
}

// Checks if a polygon zone contains annotations (markups)
export function polygonContainsAnnotation(
  instance: WebViewerInstance,
  polygonAnnotation: Core.Annotations.PolygonAnnotation,
): boolean {
  if (!polygonAnnotation) return false;

  const pageNumber = polygonAnnotation.PageNumber;
  const polygonPoints = polygonAnnotation.getPath();

  if (!polygonPoints || polygonPoints.length === 0) return false;

  const pageAnnotations = instance.Core.annotationManager
    .getAnnotationsList()
    .filter((annotation) => annotation.PageNumber === pageNumber && annotation.Id !== polygonAnnotation.Id);

  for (const annotation of pageAnnotations) {
    const isUserAnnotation =
      annotation.ToolName === PDFTronTools.CROSSOUT ||
      annotation.ToolName === PDFTronTools.MARQUEE_ZONE ||
      annotation.ToolName === PDFTronTools.ZONE;

    if (isUserAnnotation) {
      // Check if the annotation is actually inside this specific polygon zone's bounds
      const annotationRect = annotation.getRect();

      // Check if all corners of the annotation are within the polygon
      const corners = [
        { x: annotationRect.x1, y: annotationRect.y1 }, // top-left
        { x: annotationRect.x2, y: annotationRect.y1 }, // top-right
        { x: annotationRect.x2, y: annotationRect.y2 }, // bottom-right
        { x: annotationRect.x1, y: annotationRect.y2 }, // bottom-left
      ];

      const isInsideThisPolygon = corners.every((corner) => pointInPolygon(corner, polygonPoints));

      if (isInsideThisPolygon) {
        return true;
      }
    }
  }
  return false;
}

// Checks if a polygon zone contains annotations, and if so, it locks it
export function managePolygonZoneLocking(polygonZone: Core.Annotations.Annotation, documentType: DocumentTypes) {
  const instance = PDFManagerFactory.getViewer(documentType)?.instance;
  if (!instance) return;

  // Check if THIS specific polygon zone contains any annotations (markups)
  polygonZone.Locked = polygonContainsAnnotation(instance, polygonZone as Core.Annotations.PolygonAnnotation);
}
